// Lead Generation System — pure helpers for the four-track production line.
//
// The console advances four INDEPENDENT tracks (Outreach, NDA, CIM, Interest)
// in any order; the headline PreleadStatus is derived server-side. These pure
// helpers back the shared action buttons + the list filter. Human status
// label/tone live in ./pipeline (reused so HomeTab stays in lockstep).

import { formatDateInTz } from "@/design-system/lib/format";
import type { PreleadStatus } from "../../api/leadGenerator";

/** Terminal close-out branches — the row is history, no forward action. */
export function isTerminalClosed(status: PreleadStatus): boolean {
  return (
    status === "BadFit" ||
    status === "Duplicate" ||
    status === "NoResponse" ||
    status === "Closed"
  );
}

/** Once escalated (or worked by the internal team) the interest track is done
 *  and the generator can no longer advance / revert / close the row. */
export function isEscalated(
  status: PreleadStatus,
  escalatedToLeadId: string | null,
): boolean {
  return status === "Escalated" || status === "Contacted" || escalatedToLeadId != null;
}

/** Milestone-state options for the multi-select list filter. Each token maps
 *  to a server-side AND predicate on the entity's own track columns — a prelead
 *  matches only when it has reached EVERY selected milestone. Bound to repeated
 *  `?statuses=` query params. Kept in lockstep with the backend token set on
 *  PreleadListQuery.Statuses. */
export const MILESTONE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "message_sent", label: "Message sent" },
  { value: "response_received", label: "Response received" },
  { value: "nda_complete", label: "NDA complete" },
  { value: "cim_received", label: "CIM received" },
  { value: "interested", label: "Interested" },
  { value: "escalated", label: "Escalated" },
  { value: "confirmed_contacted", label: "Confirmed contacted" },
  { value: "confirmed_interested", label: "Confirmed interested" },
];

/** A readable display name for a prelead row. Business name → contact name →
 *  first line of the raw paste → listing URL host → "Prelead — <date>". Empty /
 *  partial captures (web-form outreach with no business/contact) are normal now,
 *  so this keeps the list usable without an "Untitled prelead". `rawText` is the
 *  summary's short snippet or, on the detail view, the full raw pasted text. */
export function preleadDisplayName(p: {
  businessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  rawText?: string | null;
  listingUrl?: string | null;
  createdAt?: string | null;
}): string {
  const business = p.businessName?.trim();
  if (business) return business;
  const contact = [p.contactFirstName, p.contactLastName].filter(Boolean).join(" ").trim();
  if (contact) return contact;
  const line = firstNonEmptyLine(p.rawText);
  if (line) return line.length > 60 ? `${line.slice(0, 60).trimEnd()}…` : line;
  const host = urlHost(p.listingUrl);
  if (host) return host;
  // INSTANT, not a wall-calendar date: Prelead inherits BaseEntity.CreatedAt,
  // whose initializer is `DateTimeOffset.UtcNow`, and PreleadService projects
  // `p.CreatedAt` verbatim onto both the summary and detail DTOs. An instant
  // must be rendered in a NAMED zone, so this goes through the house helper
  // rather than a bare toLocaleDateString(), which took the browser's zone and
  // locale and made this label disagree with every sibling date on the same
  // screens (PreleadDetailPanel / PreleadsTab).
  //
  // The `null` zone is DELIBERATE, not an oversight: the portal has no viewer
  // timezone to resolve — PortalUser has no timezone column, PortalMeDto has no
  // TimeZone field, and portal/api/types.ts exposes no such property — so
  // FALLBACK_TIMEZONE (America/Phoenix) is the correct and only answer here.
  // This is also a PURE module with no React, so it could not read a hook even
  // if one existed. What would change it: a server-side timezone on PortalUser
  // surfaced through /me. At that point this function grows a trailing
  // `tz?: string | null` parameter and its two callers thread the viewer's zone
  // in; the `null` default keeps every other call site byte-identical.
  const created = formatDateInTz(p.createdAt, null);
  return created ? `Prelead — ${created}` : "Prelead";
}

function firstNonEmptyLine(text?: string | null): string | null {
  if (!text) return null;
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function urlHost(url?: string | null): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed).hostname || null;
  } catch {
    return null;
  }
}

/** A green "confirmed" milestone chip fed back from the escalated Lead. */
export interface ConfirmedMilestone {
  key: "confirmed_contacted" | "confirmed_interested";
  label: string;
}

/** The confirmed-outcome chips to render for a prelead — shown only when the
 *  matching timestamp is set. Same shape on the summary row + the detail view,
 *  so the milestone-chip rendering stays in lockstep. */
export function confirmedMilestones(p: {
  confirmedContactedAtUtc: string | null;
  confirmedInterestedAtUtc: string | null;
}): ConfirmedMilestone[] {
  const chips: ConfirmedMilestone[] = [];
  if (p.confirmedContactedAtUtc)
    chips.push({ key: "confirmed_contacted", label: "Confirmed contacted" });
  if (p.confirmedInterestedAtUtc)
    chips.push({ key: "confirmed_interested", label: "Confirmed interested" });
  return chips;
}
